import { Coins } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../../../components/ui/index.js';
import { useAnyPermission } from '../../../hooks/usePermission.js';
import { cn } from '../../../lib/cn.js';
import { formatNumber } from '../../../lib/intl.js';
import { formatMoney } from '../../../lib/money.js';

import { SaleCostingDialog, percent, useProfitDocument, type SaleRef } from './SaleCostingDialog.js';

export interface SaleProfitCardProps {
  /** The document this page or dialog is showing. */
  sale: SaleRef;
  /** Extra classes for the container, e.g. tighter padding inside a dialog. */
  className?: string;
}

/**
 * What one sale earned, on the sale's own page — revenue, cost, profit, margin,
 * and a way into the line-by-line costing behind them.
 *
 * `profits.read` is the gate — or `profits.report`, which is the wider grant and
 * so covers one sale as well; the route accepts either for the same reason. It
 * is checked BEFORE the request rather than after it: a user without the
 * permission never asks the server what the shop makes, so there is no 403 to
 * leak the question. A document the profit engine
 * has nothing to say about — a draft invoice, a parked sale — answers 404, and
 * that renders nothing at all, because "no profit figures yet" is a state of
 * the document, not a failure worth a red box.
 *
 * A document is single-currency, so every figure here is in `doc.currencyCode`
 * and nothing is ever added across currencies.
 */
export function SaleProfitCard({ sale, className }: SaleProfitCardProps): JSX.Element | null {
  const { t, i18n } = useTranslation('reports');
  const locale = i18n.language;
  const canRead = useAnyPermission(['profits.read', 'profits.report']);
  const [open, setOpen] = useState(false);
  // Passing null keeps the hook mounted (rules of hooks) and idle — the request
  // is never made without the permission.
  const { doc, error } = useProfitDocument(canRead ? sale : null);

  if (!canRead) return null;
  if (error) {
    return (
      <p className={cn('text-xs text-fg-muted', className)} role="status">
        {t('profit.doc.loadFailed')}
      </p>
    );
  }
  // Nothing while the answer is on its way: a skeleton that usually resolves to
  // "render nothing" (404) would only flash a box that then disappears.
  if (!doc) return null;

  const money = (amount: string, approximate = false): string =>
    formatMoney(amount, { locale, currency: doc.currencyCode, approximate });

  // cn, not string concatenation: a caller's `p-3` has to actually beat the
  // default `p-4`, and Tailwind's own rule order would otherwise decide.
  return (
    <div className={cn('glass-panel rounded-xl border p-4', className)}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="flex items-center gap-2 text-sm font-semibold">
          <Coins className="h-4 w-4 text-fg-muted" aria-hidden />
          {t('profit.doc.title')}
        </h2>
        <Button variant="secondary" size="sm" onClick={() => setOpen(true)}>
          {t('profit.doc.breakdown')}
        </Button>
      </div>

      <dl className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Figure label={t('profit.kpi.revenue')} value={money(doc.revenue)} />
        {/* ≈ on the cost, as the report shows it: the sale's own cost was not
            recorded, so this is today's — close, not exact. */}
        <Figure
          label={t('profit.kpi.cost')}
          value={money(doc.cost, doc.isEstimate)}
          title={doc.isEstimate ? t('profit.estimateRow') : undefined}
        />
        <Figure
          label={t('profit.kpi.profit')}
          value={money(doc.profit)}
          className={Number(doc.profit) < 0 ? 'text-danger' : 'text-success'}
        />
        <Figure
          label={t('profit.kpi.margin')}
          value={doc.marginPercent === null ? '—' : percent(doc.marginPercent, locale)}
        />
      </dl>

      {doc.excludedLineCount > 0 && (
        <p className="mt-2 text-xs text-warning">
          {t('profit.doc.excludedNote', { n: formatNumber(doc.excludedLineCount, { locale }) })}
        </p>
      )}
      {doc.isEstimate && (
        <p className="mt-2 text-xs text-fg-muted">{t('profit.estimateNote')}</p>
      )}

      {/* The lines are already in hand — `preloaded` stops the dialog buying
          the same costing a second time. */}
      <SaleCostingDialog
        sale={open ? sale : null}
        preloaded={doc}
        onClose={() => setOpen(false)}
      />
    </div>
  );
}

interface FigureProps {
  label: string;
  value: string;
  className?: string;
  title?: string;
}

function Figure({ label, value, className, title }: FigureProps): JSX.Element {
  return (
    <div>
      <dt className="text-xs text-fg-muted">{label}</dt>
      <dd
        className={`font-mono text-base font-semibold tabular-nums ${className ?? ''}`}
        title={title}
      >
        {value}
      </dd>
    </div>
  );
}
